import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../ui/Button';

export default function HeroSection() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToContent = () => {
    if (scrollRef.current) {
      window.scrollTo({
        top: scrollRef.current.offsetHeight - 80,
        behavior: 'smooth',
      });
    }
  };

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <div className="relative h-screen min-h-[650px] bg-gray-900 text-white overflow-hidden" ref={scrollRef}>
      {/* Background image with gradient overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="/images/bedroom-background.png" 
          alt="Mikhael Homestay Bedroom" 
          className="w-full h-full object-cover object-center scale-[1.02]" // Slight scale to avoid white edges on animation
        />
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/20 z-10"
        ></motion.div>
      </div>

      {/* Hero content */}
      <div className="relative z-20 h-full flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="max-w-4xl mx-auto"
          variants={container}
          initial="hidden"
          animate="show"
        >
          <motion.h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold mb-6 tracking-tight drop-shadow-lg font-manrope"
            variants={item}
          >
            Welcome to <span className="text-blue-400">Mikhael Homestay</span>
          </motion.h1>
          
          <motion.p 
            className="text-lg sm:text-xl md:text-2xl mb-10 max-w-2xl mx-auto drop-shadow-md font-medium text-white/90"
            variants={item}
          >
            Your perfect home away from home in Segamat, Johor
          </motion.p>
          
          <motion.div 
            variants={item}
            className="flex flex-wrap justify-center gap-4"
          >
            <Button 
              href="/#booking-platforms"
              variant="primary"
              size="lg"
              className="min-w-[160px] shadow-lg"
            >
              Book Now
            </Button>
            
            <Button 
              variant="outline"
              size="lg"
              className="min-w-[160px] bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20"
              onClick={scrollToContent}
            >
              Explore
            </Button>
          </motion.div>
        </motion.div>

        {/* Scroll down indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            delay: 1.5,
            duration: 0.6,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
          onClick={scrollToContent}
        >
          <div className="flex flex-col items-center cursor-pointer group">
            <span className="text-sm mb-2 font-medium text-white/80 group-hover:text-white transition-colors duration-300">Scroll Down</span>
            <svg 
              className="w-6 h-6 animate-bounce text-white/80 group-hover:text-white transition-colors duration-300" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M19 14l-7 7m0 0l-7-7m7 7V3" 
              />
            </svg>
          </div>
        </motion.div>
        
        {/* Decorative elements */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/30 to-transparent z-20" />
      </div>
    </div>
  );
}
